import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import type { CreateRelationRequest, RelationId, RelationUpdateRequest } from './api'
import { createRelation, getAllRelations, getRelation, getRelationTemplates, updateRelation } from './endpoints'

const QueryKey = {
  relations: () => ['relations'] as const,
  relation: (relationId: RelationId) => ['relation', relationId] as const,
  relationTemplates: () => ['relationTemplates'] as const,
}

export const useRelations = () => {
  return useQuery({
    queryKey: QueryKey.relations(),
    queryFn: () => getAllRelations(),
  })
}

export const useRelation = (relationId: RelationId | undefined) => {
  return useQuery({
    queryKey: QueryKey.relation(relationId!),
    queryFn: () => getRelation(relationId!),
    enabled: !!relationId,
  })
}

export const useUpdateRelation = (relationId: RelationId) => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (updateRequest: RelationUpdateRequest[]) => updateRelation(relationId, updateRequest),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: QueryKey.relation(relationId) })
      queryClient.invalidateQueries({ queryKey: QueryKey.relations() })
    },
  })
}

export const useCreateRelation = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (relation: CreateRelationRequest) => createRelation(relation),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: QueryKey.relations() })
    },
  })
}

export const useRelationTemplates = () => {
  return useQuery({
    queryKey: QueryKey.relationTemplates(),
    queryFn: () => getRelationTemplates(),
  })
}
